import { useState, useCallback } from 'react';
import { notification } from 'antd';
import { pdfjs } from 'react-pdf';
import { t } from '../../../i18n/i18n';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

export const useJobDescription = () => {
  const [processingPDF, setProcessingPDF] = useState(false);
  const [extractedText, setExtractedText] = useState('');

  const extractTextFromPDF = useCallback(async (file) => {
    if (!file) return null;

    try {
      setProcessingPDF(true);
      setExtractedText('');

      return new Promise((resolve, reject) => {
        const fileReader = new FileReader();

        fileReader.onload = async (e) => {
          try {
            const typedarray = new Uint8Array(e.target.result);
            const pdf = await pdfjs.getDocument(typedarray).promise;
            let fullText = '';

            for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
              const page = await pdf.getPage(pageNum);
              const textContent = await page.getTextContent();
              const pageText = textContent.items.map((item) => item.str).join(' ');
              fullText += pageText + '\n\n';
            }

            const cleanedText = fullText
              .replace(/\s+/g, ' ')
              .replace(/\n{3,}/g, '\n\n')
              .trim();

            setExtractedText(cleanedText);
            resolve(cleanedText);
          } catch (error) {
            console.error('Error extracting text from PDF:', error);
            reject(error);
          }
        };

        fileReader.onerror = (error) => {
          console.error('Error reading file:', error);
          reject(error);
        };

        fileReader.readAsArrayBuffer(file);
      });
    } catch (error) {
      notification.error({
        message: t('CHAT_ERROR_EXTRACT_PDF'),
        description: t('CHAT_ERROR_EXTRACT_PDF_DESC'),
      });
      return null;
    } finally {
      setProcessingPDF(false);
    }
  }, []);

  const validatePDFFile = useCallback((file) => {
    if (!file) {
      notification.error({
        message: t('CHAT_ERROR_NO_FILE'),
        description: t('CHAT_ERROR_NO_FILE_DESC'),
      });
      return false;
    }

    const isPDF = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPDF) {
      notification.error({
        message: t('CHAT_ERROR_NOT_PDF'),
        description: t('CHAT_ERROR_NOT_PDF_DESC'),
      });
      return false;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      notification.error({
        message: t('CHAT_ERROR_FILE_TOO_LARGE'),
        description: t('CHAT_ERROR_FILE_TOO_LARGE_DESC'),
      });
      return false;
    }

    return true;
  }, []);

  const resetJobDescription = useCallback(() => {
    setExtractedText('');
    setProcessingPDF(false);
  }, []);

  return {
    processingPDF,
    extractedText,
    extractTextFromPDF,
    validatePDFFile,
    resetJobDescription,
  };
};
